import {
  DbotEventDispatcher,
  DecisionContext,
  makeCountryRadioFieldModel,
} from "../common/commonDomain";
import { makeAutoObservable } from "mobx";
import { DecisionType } from "../../common/decisions";
import {
  BooleanFieldModel,
  OptionFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "../../common/commonForms";
import { getRoleTypeLabel, RoleType } from "../../types/enums/RoleType";
import { emailValidator, PhoneNumber } from "../../common/common";
import { ProjectCompanyRole, sortedProjectBoardRoles } from "../common/commonBoardMeetingDomain";
import { newUuid } from "../../utils/uuid";
import { TabButtonProps } from "../../components-v2/Tabs";
import { isNorwegianOrgNo } from "../../utils/orgNo";
import { DropdownMenuItemProps, DropdownMenuItemType } from "../../components-v2/Dropdown";
import { TMessage } from "../../i18n/tt";
import { IconName } from "../../components-v2/iconMapping";
import { compact, identity, sortBy } from "lodash";
import { confirmAction, thunk } from "../../events/eventsDomain";
import { noDialog } from "../summary/summaryDomain";
import { boardRoles, validateName } from "../../utils/Roles";
import { CompanyRole } from "../../types/CompanyInfo";
import { Language } from "../../i18n/languages";
import { PhoneInputModel } from "../../components-v2/PhoneInput";
import { isBlank, unblank } from "../../utils/strings";
import { ActorsSelectorModel, ActorType, loadActors } from "../../types/SelectActors";
import { resolveUrnReference, toCompanyRoleReference } from "../../utils/UrnReference";
import { IdentificationForPerson } from "../summary/SummaryController";
import { dNumberValidator, norwegianSocialSecurityNumberValidator } from "../../utils/other";
import { DocumentType } from "../../common/documents";

export type PersonalDeputyFor = { id: string; name: string };

export enum ElectionOfBoardType {
  reElectBoardMembers = "reElectBoardMembers",
  removeAllBoardMembers = "removeAllBoardMembers",
  updateBoardMember = "updateBoardMember",
  addBoardMember = "addBoardMember",
  removeBoardMember = "removeBoardMember",
}

export type ElectionOfBoardMode =
  | ReElectBoardMembers
  | RemoveAllBoardMembers
  | UpdateBoardMember
  | AddBoardMember
  | RemoveBoardMember;

export type ReElectBoardMembers = { type: ElectionOfBoardType.reElectBoardMembers };
export type RemoveAllBoardMembers = { type: ElectionOfBoardType.removeAllBoardMembers };
export type UpdateBoardMember = {
  type: ElectionOfBoardType.updateBoardMember;
  id: string;
  companyRoleId?: number;
  role: RoleType;
  name: string;
  nationality?: string;
  socialSecurityNumber?: string;
  dnr?: string;
  personalDeputyFor?: PersonalDeputyFor[];
};
export type AddBoardMember = {
  type: ElectionOfBoardType.addBoardMember;
  id: string;
  companyRoleId?: number;
  role: RoleType;
  name: string;
  nationality?: string;
  socialSecurityNumber?: string;
  dnr?: string;
  email?: string;
  phoneNumber?: PhoneNumber;
  personalDeputyFor?: PersonalDeputyFor[];
};
export type RemoveBoardMember = {
  type: ElectionOfBoardType.removeBoardMember;
  id: string;
  companyRoleId?: number;
  name: string;
  socialSecurityNumber?: string;
};

export class ElectionOfBoardChangeModel {
  id: string = newUuid();
  data: ElectionOfBoardMode;

  constructor(data: ElectionOfBoardMode) {
    this.data = data;

    makeAutoObservable(this, {}, { autoBind: true });
  }

  onSubmitEdit(data: ElectionOfBoardMode) {
    this.data = data;
  }

  get isReElectionOfBoardMembers() {
    return this.data.type === ElectionOfBoardType.reElectBoardMembers;
  }

  get isRemoveAllBoardMembers() {
    return this.data.type === ElectionOfBoardType.removeAllBoardMembers;
  }

  toModel(): ElectionOfBoardMode {
    switch (this.data.type) {
      case ElectionOfBoardType.addBoardMember:
      case ElectionOfBoardType.updateBoardMember:
        return {
          ...this.data,
          personalDeputyFor: this.data.personalDeputyFor?.map((personal) => personal),
        };
      default:
        return this.data;
    }
  }
}

export type ElectionOfCompanyBoard = {
  type: DecisionType.electionOfCompanyBoard;

  boardChanges: ElectionOfBoardMode[];
  boardMeetingProtocolShowCompanyBoard: boolean;
  noticeGeneralMeetingShowCompanyBoard: boolean;
  genderCompositionRequirement?: boolean;
};

export type NewCompanyRole = {
  id: string;
  companyRoleId?: number;
  role: RoleType;
  name: string;
  nationality?: string;
  socialSecurityNumber?: string;
  dnr?: string;
  personalDeputyFor?: PersonalDeputyFor[];
};

export type CompanyBoardPreviewTab = "currentBoard" | "newBoard";

export type ElectionOfBoardDialogState =
  | { type: "none" }
  | { type: "addBoardMember"; model: AddToElectionOfBoardModel }
  | { type: "addBoardMemberFromPortfolio"; model: ActorsSelectorModel }
  | { type: "updateBoardMember"; model: UpdateElectionOfBoardModel }
  | { type: "removeBoardMember"; model: RemoveElectionOfBoardModel };

export class ElectionOfCompanyBoardForm {
  readonly type = DecisionType.electionOfCompanyBoard;

  boardChanges: ElectionOfBoardChangeModel[];
  boardMeetingProtocolShowCompanyBoard: BooleanFieldModel;
  noticeGeneralMeetingShowCompanyBoard: BooleanFieldModel;
  genderCompositionRequirement: BooleanFieldModel;

  boardChangeDialogState: ElectionOfBoardDialogState = noDialog();
  selectedCompanyBoardPreviewTab: CompanyBoardPreviewTab = "currentBoard";

  constructor(
    readonly dispatch: DbotEventDispatcher,
    readonly context: DecisionContext,
    data?: ElectionOfCompanyBoard,
  ) {
    this.boardChanges =
      data?.boardChanges.map((boardChange) => new ElectionOfBoardChangeModel(boardChange)) ?? [];

    this.boardMeetingProtocolShowCompanyBoard = new BooleanFieldModel({
      value: data?.boardMeetingProtocolShowCompanyBoard ?? false,
    });
    this.noticeGeneralMeetingShowCompanyBoard = new BooleanFieldModel({
      value: data?.noticeGeneralMeetingShowCompanyBoard ?? false,
    });
    this.genderCompositionRequirement = new BooleanFieldModel({
      value: data?.genderCompositionRequirement,
      required: true,
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get containsBoardMeetingMinutes() {
    return this.context.currentDocuments.some((a) => a.type === DocumentType.board_meeting);
  }

  get containsNoticeGeneralMeeting() {
    return this.context.currentDocuments.some(
      (a) => a.type === DocumentType.notice_general_meeting,
    );
  }

  get showCurrentBoard(): boolean {
    return this.selectedCompanyBoardPreviewTab === "currentBoard";
  }

  closeDialog() {
    this.boardChangeDialogState = noDialog();
  }

  get isNorwegianCompany() {
    return isNorwegianOrgNo(this.context.orgNo);
  }

  get companyName(): string {
    return this.context.companyInfo.company.name;
  }

  get isNewBoardTabDisabled() {
    return !this.containsBoardChanges || this.isNewCompanyBoardEmpty;
  }

  setActiveTab(tab: CompanyBoardPreviewTab) {
    this.selectedCompanyBoardPreviewTab = tab;
  }

  get companyroles() {
    return this.context.companyInfo.roles;
  }

  get roles() {
    return sortedProjectBoardRoles(this.context.projectRoles);
  }

  get newCompanyBoard() {
    return this.boardAfterChanges(this.boardChanges);
  }

  get isNewBoardMissingChairperson() {
    return this.newCompanyBoard.every((a) => a.role !== RoleType.CHAIRPERSON);
  }

  boardAfterChanges(boardChanges: ElectionOfBoardChangeModel[]) {
    let newBoard = toNewCompanyRoles(this.roles, this.companyroles);
    boardChanges.forEach((boardChange) => {
      newBoard = changeCompanyBoard(boardChange.data, newBoard);
    });
    return sortedNewCompanyRoles(newBoard);
  }

  get containsBoardChanges() {
    return this.boardChanges.length > 0;
  }

  get isNewCompanyBoardEmpty() {
    return this.newCompanyBoard.length === 0;
  }

  get isReElectionOfBoardMembers() {
    return (
      this.containsBoardChanges &&
      this.boardChanges.some((model) => model.isReElectionOfBoardMembers)
    );
  }

  get containsRemoveAllBoardMembers() {
    return this.boardChanges.some((model) => model.isRemoveAllBoardMembers);
  }

  addBoardChange(boardChange: ElectionOfBoardMode) {
    this.boardChanges.push(new ElectionOfBoardChangeModel(boardChange));
    this.setActiveTab(this.isNewBoardTabDisabled ? "currentBoard" : "newBoard");
  }

  editBoardChange(locale: Language, model: ElectionOfBoardChangeModel) {
    switch (model.data.type) {
      case ElectionOfBoardType.updateBoardMember:
        this.boardChangeDialogState = {
          type: "updateBoardMember",
          model: new UpdateElectionOfBoardModel(
            locale,
            this.newCompanyBoard,
            model.onSubmitEdit,
            this.closeDialog,
            model.data,
          ),
        };
        break;
      case ElectionOfBoardType.addBoardMember:
        this.boardChangeDialogState = {
          type: "addBoardMember",
          model: new AddToElectionOfBoardModel(
            locale,
            this.newCompanyBoard,
            model.onSubmitEdit,
            this.closeDialog,
            model.data,
          ),
        };
        break;
    }
  }

  onSelectBoardChange(locale: Language, type: ElectionOfBoardType) {
    switch (type) {
      case ElectionOfBoardType.removeAllBoardMembers:
      case ElectionOfBoardType.reElectBoardMembers:
        this.addBoardChange({ type });
        break;
      case ElectionOfBoardType.removeBoardMember:
        this.boardChangeDialogState = {
          type: "removeBoardMember",
          model: new RemoveElectionOfBoardModel(
            locale,
            this.newCompanyBoard,
            this.addBoardChange,
            this.closeDialog,
          ),
        };
        break;
      case ElectionOfBoardType.updateBoardMember:
        this.boardChangeDialogState = {
          type: "updateBoardMember",
          model: new UpdateElectionOfBoardModel(
            locale,
            this.newCompanyBoard,
            this.addBoardChange,
            this.closeDialog,
          ),
        };
        break;
      case ElectionOfBoardType.addBoardMember:
        this.boardChangeDialogState = {
          type: "addBoardMember",
          model: new AddToElectionOfBoardModel(
            locale,
            this.newCompanyBoard,
            this.addBoardChange,
            this.closeDialog,
          ),
        };
        break;
    }
  }

  openAddNewShareholderFromPortfolioDialog(locale: Language) {
    this.boardChangeDialogState = {
      type: "addBoardMemberFromPortfolio",
      model: openAddBoardMemberFromPortfolioDialog(
        this.context.orgNo,
        this.dispatch,
        this.newCompanyBoard,
        (data) => {
          this.boardChangeDialogState = {
            type: "addBoardMember",
            model: new AddToElectionOfBoardModel(
              locale,
              this.newCompanyBoard,
              this.addBoardChange,
              this.closeDialog,
              data,
            ),
          };
        },
        this.closeDialog,
      ),
    };
  }

  showDeleteBoardChangeDialog(id: string) {
    this.dispatch(
      confirmAction({
        text: { key: "decisions.electionOfCompanyBoard.boardChange.delete.confirm" },
        onConfirm: [thunk(() => this.deleteBoardchange(id))],
      }),
    );
  }

  deleteBoardchange(id: string) {
    this.boardChanges = this.boardChanges.filter((d) => d.id !== id);
    this.setActiveTab(this.isNewBoardTabDisabled ? "currentBoard" : "newBoard");
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isValid() {
    return (
      this.containsBoardChanges &&
      !this.isNewCompanyBoardEmpty &&
      this.boardMeetingProtocolShowCompanyBoard.isValid &&
      this.noticeGeneralMeetingShowCompanyBoard.isValid &&
      this.genderCompositionRequirement.isValid
    );
  }

  validate(): boolean {
    this.boardMeetingProtocolShowCompanyBoard.validate();
    this.noticeGeneralMeetingShowCompanyBoard.validate();
    this.genderCompositionRequirement.validate();
    return this.isValid;
  }
}

export class AddToElectionOfBoardModel {
  data?: AddBoardMember;
  roleType: RadioFieldModel<RoleType | null>;
  name: StringFieldModel;
  nationality: RadioFieldModel<{ code: string; name: string }>;
  socialSecurityNumber: StringFieldModel;
  dnr: StringFieldModel;
  email: StringFieldModel;
  phoneNumber: PhoneInputModel;
  personalDeputyFor: OptionFieldModel<PersonalDeputyFor>;

  constructor(
    readonly locale: Language,
    readonly newRoles: NewCompanyRole[],
    readonly onSubmit: (data: ElectionOfBoardMode) => void,
    readonly onClose: () => void,
    data?: AddBoardMember,
  ) {
    this.data = data;
    this.roleType = new RadioFieldModel({
      value: data?.role ?? null,
      options: boardRoles,
      idFn: identity,
      labelFn: (item) =>
        item !== null
          ? getRoleTypeLabel(item)
          : { key: "decisions.electionOfCompanyBoard.boardChange.dialog.role.placeholder" },
      required: true,
    });
    this.name = new StringFieldModel({
      value: data?.name,
      required: true,
      validator: validateName,
    });
    this.nationality = makeCountryRadioFieldModel(locale, data?.nationality);
    this.socialSecurityNumber = new StringFieldModel({
      value: data?.socialSecurityNumber,
      validator: (value) => {
        if (this.isExistingSsn(value)) {
          return {
            key: "decisions.electionOfCompanyBoard.boardChange.dialog.socialSecurityNumber.existing-ssn",
          };
        } else if (this.nationality.value.code === "NO") {
          return norwegianSocialSecurityNumberValidator(value);
        }
      },
    });
    this.dnr = new StringFieldModel({ value: data?.dnr, validator: dNumberValidator });
    this.email = new StringFieldModel({ value: data?.email, validator: emailValidator });
    this.phoneNumber = new PhoneInputModel({ value: data?.phoneNumber, locale });

    this.personalDeputyFor = new OptionFieldModel({
      value: data?.personalDeputyFor ?? [],
      options: toPersonalDeputyFor(this.newRoles),
      idFn: (item) => item.id,
      labelFn: (item) => ({ translated: item.name }),
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  isExistingSsn(value: string) {
    return !isBlank(value) && this.newRoles.some((a) => a.socialSecurityNumber === value);
  }

  get isDeputyMember() {
    return this.roleType.value === RoleType.DEPUTY_MEMBER;
  }

  submit() {
    if (this.validate()) {
      this.onSubmit(this.toAddBoardMember());
      this.onClose();
    }
  }

  toAddBoardMember(): AddBoardMember {
    return {
      type: ElectionOfBoardType.addBoardMember,
      id: this.data?.id ?? newUuid(),
      companyRoleId: this.data?.companyRoleId,
      role: this.roleType.value ?? RoleType.MEMBER,
      name: this.name.validatedValue,
      nationality: this.nationality.value.code,
      socialSecurityNumber: unblank(this.socialSecurityNumber.validatedValue),
      dnr: this.dnr.isEmpty ? undefined : this.dnr.validatedValue,
      email: this.email.isEmpty ? undefined : this.email.validatedValue,
      phoneNumber: this.phoneNumber.toValidPhoneNumber(),
      personalDeputyFor: this.isDeputyMember ? this.personalDeputyFor.selected : undefined,
    };
  }

  get isValid() {
    return (
      this.roleType.isValid &&
      this.name.isValid &&
      this.nationality.isValid &&
      this.socialSecurityNumber.isValid &&
      this.dnr.isValid &&
      this.email.isValid &&
      this.phoneNumber.isValid &&
      this.personalDeputyFor.isValid
    );
  }

  validate(): boolean {
    this.roleType.validate();
    this.name.validate();
    this.nationality.validate();
    this.socialSecurityNumber.validate();
    this.dnr.validate();
    this.email.validate();
    this.phoneNumber.validate();
    this.personalDeputyFor.validate();
    return this.isValid;
  }
}

export class UpdateElectionOfBoardModel {
  data?: UpdateBoardMember;
  selectedMember: RadioFieldModel<NewCompanyRole | null>;
  roleType: RadioFieldModel<RoleType | null>;
  nationality: RadioFieldModel<{ code: string; name: string }>;
  socialSecurityNumber: StringFieldModel;
  dnr: StringFieldModel;
  personalDeputyFor: OptionFieldModel<PersonalDeputyFor>;

  constructor(
    readonly locale: Language,
    readonly newRoles: NewCompanyRole[],
    readonly onSubmit: (data: ElectionOfBoardMode) => void,
    readonly onClose: () => void,
    data?: UpdateBoardMember,
  ) {
    this.data = data;
    this.selectedMember = new RadioFieldModel({
      value: null,
      options: newRoles,
      idFn: (item) => item?.id ?? "null",
      labelFn: (item) =>
        item !== null
          ? { translated: item.name }
          : {
              key: "decisions.electionOfCompanyBoard.boardChange.dialog.selectBoardMember.placeholder",
            },
      required: data === undefined,
      onChange: (item) => {
        this.socialSecurityNumber.setAndCommitInputValue(item?.socialSecurityNumber ?? "");
      },
    });
    this.roleType = new RadioFieldModel({
      value: this.data?.role ?? null,
      options: boardRoles,
      idFn: identity,
      labelFn: (item) =>
        item !== null
          ? getRoleTypeLabel(item)
          : { key: "decisions.electionOfCompanyBoard.boardChange.dialog.role.placeholder" },
      required: true,
    });

    this.nationality = makeCountryRadioFieldModel(locale, this.data?.nationality);
    this.socialSecurityNumber = new StringFieldModel({
      value: this.data?.socialSecurityNumber,
      validator: (value) => {
        if (this.isExistingSsn(value)) {
          return {
            key: "decisions.electionOfCompanyBoard.boardChange.dialog.socialSecurityNumber.existing-ssn",
          };
        } else if (this.nationality.value.code === "NO") {
          return norwegianSocialSecurityNumberValidator(value);
        }
      },
    });
    this.dnr = new StringFieldModel({
      value: this.data?.dnr,
      validator: dNumberValidator,
    });

    this.personalDeputyFor = new OptionFieldModel({
      value: this.data?.personalDeputyFor ?? [],
      options: () => this.personalDeputyForOptions,
      idFn: (item) => item.id,
      labelFn: (item) => ({ translated: item.name }),
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  isExistingSsn(value: string) {
    return (
      !isBlank(value) &&
      this.newRolesExcludingSelectedMember.some((a) => a.socialSecurityNumber === value)
    );
  }

  get newRolesExcludingSelectedMember() {
    const selectedId = this.data?.id ?? this.selectedMember.value?.id;
    const selectedCompanyRoleIdId =
      this.data?.companyRoleId ?? this.selectedMember.value?.companyRoleId;
    return selectedCompanyRoleIdId !== undefined
      ? this.newRoles.filter((person) => selectedCompanyRoleIdId !== person.companyRoleId)
      : selectedId !== undefined
        ? this.newRoles.filter((person) => selectedId !== person.id)
        : this.newRoles;
  }

  get personalDeputyForOptions(): PersonalDeputyFor[] {
    return toPersonalDeputyFor(this.newRolesExcludingSelectedMember);
  }

  get isEdit() {
    return this.data !== undefined;
  }

  get isDeputyMember() {
    return this.roleType.value === RoleType.DEPUTY_MEMBER;
  }

  submit() {
    if (this.validate()) {
      this.onSubmit(this.toUpdateBoardMember());
      this.onClose();
    }
  }

  toUpdateBoardMember(): UpdateBoardMember {
    return {
      type: ElectionOfBoardType.updateBoardMember,
      id: this?.data ? this.data.id : (this.selectedMember.value?.id ?? ""),
      companyRoleId: this?.data
        ? this?.data?.companyRoleId
        : this.selectedMember.value?.companyRoleId,
      name: this?.data ? this.data.name : (this.selectedMember.value?.name ?? ""),
      role: this.roleType.value ?? RoleType.MEMBER,
      nationality: this.nationality.value.code,
      socialSecurityNumber: unblank(this.socialSecurityNumber.validatedValue),
      dnr: this.dnr.isEmpty ? undefined : this.dnr.validatedValue,
      personalDeputyFor: this.isDeputyMember ? this.personalDeputyFor.selected : undefined,
    };
  }

  get isValid() {
    return (
      this.selectedMember.isValid &&
      this.roleType.isValid &&
      this.nationality.isValid &&
      this.socialSecurityNumber.isValid &&
      this.dnr.isValid &&
      this.personalDeputyFor.isValid
    );
  }

  validate(): boolean {
    this.selectedMember.validate();
    this.roleType.validate();
    this.nationality.validate();
    this.socialSecurityNumber.validate();
    this.dnr.validate();
    this.personalDeputyFor.validate();
    return this.isValid;
  }
}

export class RemoveElectionOfBoardModel {
  selectedMember: RadioFieldModel<NewCompanyRole | null>;

  constructor(
    readonly locale: Language,
    readonly newRoles: NewCompanyRole[],
    readonly onSubmit: (data: ElectionOfBoardMode) => void,
    readonly onClose: () => void,
  ) {
    this.selectedMember = new RadioFieldModel({
      value: null,
      options: newRoles,
      idFn: (item) => item?.id ?? "null",
      labelFn: (item) =>
        item !== null
          ? { translated: item.name }
          : {
              key: "decisions.electionOfCompanyBoard.boardChange.dialog.selectBoardMember.placeholder",
            },
      required: true,
    });

    makeAutoObservable(this, {}, { autoBind: true });
  }

  submit() {
    if (this.validate()) {
      this.onSubmit(this.toRemoveBoardMember());
      this.onClose();
    }
  }

  toRemoveBoardMember(): RemoveBoardMember {
    const selectedBoardMember = this.selectedMember.value;
    return {
      type: ElectionOfBoardType.removeBoardMember,
      id: selectedBoardMember?.id ?? "",
      companyRoleId: selectedBoardMember?.companyRoleId,
      name: selectedBoardMember?.name ?? "",
      socialSecurityNumber: unblank(selectedBoardMember?.socialSecurityNumber ?? ""),
    };
  }

  get isValid() {
    return this.selectedMember.isValid;
  }

  validate(): boolean {
    this.selectedMember.validate();
    return this.isValid;
  }
}

export function toNewCompanyRoles(
  boardMembers: ProjectCompanyRole[],
  companyRoles: CompanyRole[],
): NewCompanyRole[] {
  return boardMembers.map((member) => ({
    id: newUuid(),
    companyRoleId: member.companyroleId,
    socialSecurityNumber: companyRoles.find((role) => role.companyroleId === member.companyroleId)
      ?.socialSecurityNumber,
    name: member.name,
    role: member.role,
  }));
}

export function sortedNewCompanyRoles(roles: NewCompanyRole[]) {
  return sortBy(
    roles,
    (r) => boardRoles.indexOf(r.role),
    (r) => r.name,
  );
}

export function changeCompanyBoard(
  boardChange: ElectionOfBoardMode,
  currentBoard: NewCompanyRole[],
): NewCompanyRole[] {
  switch (boardChange.type) {
    case ElectionOfBoardType.reElectBoardMembers:
      return currentBoard;
    case ElectionOfBoardType.removeAllBoardMembers:
      return [];
    case ElectionOfBoardType.updateBoardMember:
      return currentBoard.map((member) =>
        (boardChange.companyRoleId !== undefined &&
          member.companyRoleId === boardChange.companyRoleId) ||
        member.id === boardChange.id
          ? {
              ...member,
              role: boardChange.role,
              nationality: boardChange.nationality,
              socialSecurityNumber: boardChange.socialSecurityNumber,
              dnr: boardChange.dnr,
              personalDeputyFor: boardChange.personalDeputyFor,
            }
          : member,
      );
    case ElectionOfBoardType.addBoardMember:
      return [
        ...currentBoard,
        {
          id: boardChange.id,
          companyRoleId: boardChange.companyRoleId,
          name: boardChange.name,
          role: boardChange.role,
          nationality: boardChange.nationality,
          socialSecurityNumber: boardChange.socialSecurityNumber,
          dnr: boardChange.dnr,
          personalDeputyFor: boardChange.personalDeputyFor,
        },
      ];
    case ElectionOfBoardType.removeBoardMember:
      return currentBoard.filter(
        (member) =>
          (boardChange.companyRoleId === undefined && member.id !== boardChange.id) ||
          (boardChange.companyRoleId !== undefined &&
            member.companyRoleId !== boardChange.companyRoleId),
      );
  }
}

export function isPersonalDeputy(member: NewCompanyRole) {
  return (
    member.role === RoleType.DEPUTY_MEMBER &&
    member.personalDeputyFor !== undefined &&
    member.personalDeputyFor.length > 0
  );
}

export function prepareShowBoardTabs(form: ElectionOfCompanyBoardForm): TabButtonProps[] {
  return [
    {
      id: "currentBoard",
      label: {
        key: "decisions.electionOfCompanyBoard.tabs.currentBoard",
        params: { n: { type: "number", value: form.roles.length } },
      },
      isActive: form.selectedCompanyBoardPreviewTab === "currentBoard",
      onSelect: () => form.setActiveTab("currentBoard"),
    },
    {
      id: "newBoard",
      label: form.containsBoardChanges
        ? {
            key: "decisions.electionOfCompanyBoard.tabs.newBoard",
            params: { n: { type: "number", value: form.newCompanyBoard.length } },
          }
        : { key: "decisions.electionOfCompanyBoard.tabs.newBoard.default" },
      isActive: form.selectedCompanyBoardPreviewTab === "newBoard",
      onSelect: () => form.setActiveTab("newBoard"),
      disabled: form.isNewBoardTabDisabled,
    },
  ];
}

export function prepareBoardChangeItems(
  locale: Language,
  form: ElectionOfCompanyBoardForm,
): DropdownMenuItemType[] {
  const updateAndRemoveActions =
    !form.containsRemoveAllBoardMembers && !form.isNewCompanyBoardEmpty
      ? [ElectionOfBoardType.updateBoardMember, ElectionOfBoardType.removeBoardMember]
      : [];
  const individualActions = [ElectionOfBoardType.addBoardMember, ...updateAndRemoveActions];
  const bulkActions =
    !form.containsBoardChanges && !form.isNewCompanyBoardEmpty
      ? [ElectionOfBoardType.reElectBoardMembers, ElectionOfBoardType.removeAllBoardMembers]
      : [];
  return compact([
    ...individualActions.map<DropdownMenuItemProps>((type) => ({
      type: "item",
      label: boardChangeItemLabel[type],
      icon: boardChangeItemIcon[type],
      onSelect: () => form.onSelectBoardChange(locale, type),
    })),
    {
      type: "label",
      label: { key: "decisions.electionOfCompanyBoard.boardChange.button.item.bulk-label" },
    },
    {
      type: "item",
      label: { key: "decisions.electionOfCompanyBoard.boardChange.button.item.add.portfolio" },
      icon: "suitcaseSimple",
      onSelect: () => form.openAddNewShareholderFromPortfolioDialog(locale),
    },
    ...bulkActions.map<DropdownMenuItemProps>((type) => ({
      type: "item",
      label: boardChangeItemLabel[type],
      icon: boardChangeItemIcon[type],
      onSelect: () => form.onSelectBoardChange(locale, type),
    })),
  ]);
}

export const boardChangeItemLabel: { [key in ElectionOfBoardType]: TMessage } = {
  [ElectionOfBoardType.updateBoardMember]: {
    key: "decisions.electionOfCompanyBoard.boardChange.button.item.update",
  },
  [ElectionOfBoardType.removeBoardMember]: {
    key: "decisions.electionOfCompanyBoard.boardChange.button.item.remove",
  },
  [ElectionOfBoardType.addBoardMember]: {
    key: "decisions.electionOfCompanyBoard.boardChange.button.item.add",
  },
  [ElectionOfBoardType.reElectBoardMembers]: {
    key: "decisions.electionOfCompanyBoard.boardChange.button.item.reElect",
  },
  [ElectionOfBoardType.removeAllBoardMembers]: {
    key: "decisions.electionOfCompanyBoard.boardChange.button.item.removeAll",
  },
};

export const boardChangeItemIcon: { [key in ElectionOfBoardType]: IconName } = {
  [ElectionOfBoardType.updateBoardMember]: "user-switch",
  [ElectionOfBoardType.removeBoardMember]: "minus",
  [ElectionOfBoardType.addBoardMember]: "plus",
  [ElectionOfBoardType.reElectBoardMembers]: "arrow-clockwise",
  [ElectionOfBoardType.removeAllBoardMembers]: "stack-minus",
};

export function boardChangeSummaryDescription(data: ElectionOfBoardMode): TMessage {
  switch (data.type) {
    case ElectionOfBoardType.addBoardMember:
      return {
        key: "decisions.electionOfCompanyBoard.boardChange.description.add",
        params: {
          name: data.name,
          role: { type: "uncapitilize", value: getRoleTypeLabel(data.role) },
        },
      };
    case ElectionOfBoardType.removeBoardMember:
      return {
        key: "decisions.electionOfCompanyBoard.boardChange.description.remove",
        params: { name: data.name },
      };
    case ElectionOfBoardType.updateBoardMember:
      return {
        key: "decisions.electionOfCompanyBoard.boardChange.description.update",
        params: {
          name: data.name,
          role: { type: "uncapitilize", value: getRoleTypeLabel(data.role) },
        },
      };
    case ElectionOfBoardType.reElectBoardMembers:
      return { key: "decisions.electionOfCompanyBoard.boardChange.description.reElect" };
    case ElectionOfBoardType.removeAllBoardMembers:
      return { key: "decisions.electionOfCompanyBoard.boardChange.description.removeAll" };
  }
}

export function toPersonalDeputyFor(newRoles: NewCompanyRole[]) {
  return newRoles
    .filter((a) => a.role !== RoleType.DEPUTY_MEMBER)
    .map((newRole) => ({
      id: newRole?.companyRoleId?.toString() ?? newRole.id,
      name: newRole.name,
    }));
}

function openAddBoardMemberFromPortfolioDialog(
  orgNo: string,
  dispatch: DbotEventDispatcher,
  newCompanyBoard: NewCompanyRole[],
  onSubmit: (boardChange: AddBoardMember) => void,
  onClose: () => void,
): ActorsSelectorModel {
  return new ActorsSelectorModel({
    title: { key: "decisions.electionOfCompanyBoard.boardChange.button.item.add.portfolio.title" },
    organisationNumber: orgNo,
    tabs: [ActorType.BOARD_MEMBER, ActorType.CEO, ActorType.SHAREHOLDER],
    excludeActors: newCompanyBoard
      .map((member) => {
        const reference = member.companyRoleId
          ? toCompanyRoleReference(member.companyRoleId)
          : undefined;
        return member.socialSecurityNumber ?? reference;
      })
      .filter((a) => a !== undefined),
    excludeCompanies: true,
    isMultiSelect: false,
    defaultCompanyFilterValue: null,
    onLoad: (type, onSuccess, onFailure) => loadActors(type, onSuccess, onFailure, dispatch, orgNo),
    onSubmit: (selectedActors) => {
      const selectedActor = selectedActors[0];
      const reference = resolveUrnReference(selectedActor.reference);
      onSubmit({
        type: ElectionOfBoardType.addBoardMember,
        id: newUuid(),
        companyRoleId:
          reference?.type === "companyroleId" && selectedActor.organisationNumber === orgNo
            ? reference.id
            : undefined,
        role: RoleType.MEMBER,
        name: selectedActor.name,
        nationality: selectedActor.nationality,
        email: selectedActor.email,
        phoneNumber: selectedActor.phoneNumber,
        socialSecurityNumber: selectedActor.socialSecurityNumber,
      });
    },
    onClose,
  });
}

export function getMissingIdentificationDocumentations(
  boardChanges: ElectionOfBoardMode[],
): IdentificationForPerson[] {
  let membersForIdentification: IdentificationForPerson[] = [];
  boardChanges.forEach((boardChange) => {
    membersForIdentification = getRoleForIdentification(boardChange, membersForIdentification);
  });
  return membersForIdentification;
}

function getRoleForIdentification(
  boardChange: ElectionOfBoardMode,
  membersForIdentification: IdentificationForPerson[],
): IdentificationForPerson[] {
  switch (boardChange.type) {
    case ElectionOfBoardType.reElectBoardMembers:
      return [];
    case ElectionOfBoardType.removeAllBoardMembers:
      return [];
    case ElectionOfBoardType.updateBoardMember: {
      const existing = membersForIdentification.find(
        (member) =>
          (member.companyRoleId !== undefined &&
            member.companyRoleId === boardChange.companyRoleId) ||
          member.id === boardChange.id,
      );
      return existing
        ? [
            ...removeRoleForIdentification(
              membersForIdentification,
              boardChange.id,
              boardChange.companyRoleId,
            ),
            toRoleForIdentification(boardChange),
          ]
        : [...membersForIdentification, toRoleForIdentification(boardChange)];
    }
    case ElectionOfBoardType.addBoardMember:
      return [...membersForIdentification, toRoleForIdentification(boardChange)];
    case ElectionOfBoardType.removeBoardMember:
      return removeRoleForIdentification(
        membersForIdentification,
        boardChange.id,
        boardChange.companyRoleId,
      );
  }
}

function removeRoleForIdentification(
  membersForIdentification: IdentificationForPerson[],
  id: string,
  companyRoleId?: number,
): IdentificationForPerson[] {
  return membersForIdentification.filter(
    (member) =>
      (companyRoleId === undefined && member.id !== id) ||
      (companyRoleId !== undefined && member.companyRoleId !== companyRoleId),
  );
}

function toRoleForIdentification(role: {
  id: string;
  companyRoleId?: number;
  name: string;
  nationality?: string;
  socialSecurityNumber?: string;
  dnr?: string;
  email?: string;
}): IdentificationForPerson {
  return {
    id: role.id,
    companyRoleId: role.companyRoleId,
    name: role.name,
    nationality: role.nationality,
    identityNumber: role.socialSecurityNumber ?? "",
    dnr: role.dnr,
    email: role.email,
  };
}
